import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, CheckCircle, XCircle, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure axios sends cookies
axios.defaults.withCredentials = true;

const REPORT_REASONS = {
  'inappropriate': 'Inappropriate Content',
  'harmful': 'Harmful/Triggering',
  'spam': 'Spam',
  'other': 'Other'
};

const ACTION_MESSAGES = {
  dismiss: 'Report dismissed',
  hide: 'Content hidden',
  delete: 'Content deleted'
};

const ContentPreview = ({ content, contentType, onView }) => {
  if (!content) {
    return (
      <Button variant="outline" size="sm" onClick={onView} className="mb-4">
        <Eye className="w-4 h-4 mr-2" />
        View Content
      </Button>
    );
  }
  return (
    <div className="mb-4 p-4 bg-stone-50 border border-stone-200 rounded-xl">
      <p className="text-sm text-stone-500 mb-1">
        Reported {contentType} by {content.is_anonymous ? 'Anonymous' : content.author_name}:
      </p>
      {content.title && <p className="font-medium text-stone-700 mb-1">{content.title}</p>}
      <p className="text-stone-600 text-sm">{content.content}</p>
    </div>
  );
};

const ReportActions = ({ disabled, onAction }) => (
  <div className="flex flex-wrap gap-2">
    <Button variant="outline" size="sm" onClick={() => onAction('dismiss')} disabled={disabled} className="text-stone-600">
      <CheckCircle className="w-4 h-4 mr-1" />
      Dismiss
    </Button>
    <Button variant="outline" size="sm" onClick={() => onAction('hide')} disabled={disabled} className="text-amber-600 border-amber-200 hover:bg-amber-50">
      <Eye className="w-4 h-4 mr-1" />
      Hide
    </Button>
    <Button variant="outline" size="sm" onClick={() => onAction('delete')} disabled={disabled} className="text-red-600 border-red-200 hover:bg-red-50">
      <XCircle className="w-4 h-4 mr-1" />
      Delete
    </Button>
  </div>
);

const ReportCard = ({ report, content, processing, onView, onAction }) => {
  const contentType = report.post_id ? 'post' : 'thread';
  const contentId = report.post_id || report.thread_id;

  return (
    <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-4 sm:p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <span className="font-medium text-stone-700">
            {REPORT_REASONS[report.reason] || report.reason}
          </span>
        </div>
        <span className="text-xs text-stone-400">
          {new Date(report.created_at).toLocaleDateString()}
        </span>
      </div>

      {report.details && (
        <p className="text-sm text-stone-600 mb-4 p-3 bg-stone-50 rounded-lg">
          "{report.details}"
        </p>
      )}

      <ContentPreview
        content={content}
        contentType={contentType}
        onView={() => onView(contentType, contentId)}
      />

      <ReportActions
        disabled={processing}
        onAction={(action) => onAction(report.id, action, contentId, contentType)}
      />
    </div>
  );
};

export const ModerationView = ({ reports, reportedContent, onActionTaken, onFetchContent }) => {
  const [processingReport, setProcessingReport] = useState(null);

  const fetchReportedContent = async (contentType, contentId) => {
    try {
      const res = await axios.get(`${API}/admin/moderation/content/${contentType}/${contentId}`);
      onFetchContent?.(contentId, res.data);
    } catch (error) {
      toast.error('Failed to fetch content');
    }
  };

  const handleModerationAction = async (reportId, action, contentId, contentType) => {
    setProcessingReport(reportId);
    try {
      await axios.post(`${API}/admin/moderation/action`, {
        report_id: reportId,
        action: action,
        content_id: contentId,
        content_type: contentType
      });
      toast.success(ACTION_MESSAGES[action] || 'Report processed');
      onActionTaken?.();
    } catch (error) {
      toast.error('Failed to process report');
    } finally {
      setProcessingReport(null);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-2xl sm:text-3xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
          Content Moderation
        </h2>
        <span className="text-stone-500 text-sm sm:text-base">
          {reports.length} pending {reports.length === 1 ? 'report' : 'reports'}
        </span>
      </div>

      {reports.length === 0 ? (
        <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8 sm:p-12 text-center">
          <Shield className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-700 mb-2">All Clear</h3>
          <p className="text-stone-500">No pending content reports to review.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              content={reportedContent[report.post_id || report.thread_id]}
              processing={processingReport === report.id}
              onView={fetchReportedContent}
              onAction={handleModerationAction}
            />
          ))}
        </div>
      )}
    </div>
  );
};
